<script setup lang="ts">
import { useQueryClient } from "@tanstack/vue-query";
import { NotificationType } from "evangelist-common/enums";
import NewTabIcon from "~/components/Icons/NewTabIcon.vue";
import type { NotificationDto } from "~~/_api_generated_";
interface Props {
  item?: NotificationDto;
}
const props = defineProps<Props>();
const queryClient = useQueryClient();

const store = useDefaultStore();

function handleCampaignDetailAction(item: NotificationDto) {
  const campaignId = item?.callToActionParam?.campaignId ?? null;
  if (campaignId) {
    useRouter().push("/campaign/" + campaignId);
  } else {
    useRouter().push("/campaigns");
  }
}

function handleCampaingVerificationAction(item: NotificationDto) {
  const campaignId = item?.callToActionParam?.campaignId ?? null;
  if (campaignId) {
    useRouter().push("/campaigns?campaignId=" + campaignId);
  } else {
    useRouter().push("/campaigns");
  }
}

const itemTypeConfig = [
  {
    // ok
    notificationType: NotificationType.CampaignJoined,
    ctaLabel: "Visit Campaign",
    alwaysShowCta: true,
  },
  {
    // ok
    notificationType: NotificationType.CampaignEnded,
    ctaLabel: "Visit Campaign",
    alwaysShowCta: true,
  },
  {
    // ok
    notificationType: NotificationType.CampaignEligible,
    ctaLabel: "Visit Campaign",
    alwaysShowCta: true,
  },
  {
    // ok
    notificationType: NotificationType.CampaignNotEligible,
    ctaLabel: "Try another wallet",
    alwaysShowCta: true,
  },
  {
    // ok
    notificationType: NotificationType.CampaignMaturity,
    ctaLabel: "Claim Rewards",
    alwaysShowCta: true,
  },
  {
    // ok
    notificationType: NotificationType.NewEligiblePosts,
    ctaLabel: "Claim Rewards",
    alwaysShowCta: true,
  },
  {
    // ok
    notificationType: NotificationType.TransactionVerifying,
    ctaLabel: "Verify Transaction",
    alwaysShowCta: true,
  },
  {
    // ok
    notificationType: NotificationType.TransactionCompleted,
    ctaLabel: "See on Blockchain",
    alwaysShowCta: true,
  },
  {
    // ok
    notificationType: NotificationType.TermsAndConditions,
    alwaysShowCta: false,
  },
  {
    notificationType: NotificationType.VlayerBadge,
    alwaysShowCta: true,
  },
  {
    notificationType: NotificationType.AdHoc,
    alwaysShowCta: false,
  },
];

const currentEventType = computed(() => {
  return (
    itemTypeConfig.find((itemType) => {
      return itemType.notificationType === props.item?.eventType;
    }) ?? null
  );
});

const computedCtaLabel = computed(() => {
  return currentEventType.value?.ctaLabel ?? "Mark as seen";
});

const computedShowCtaLabel = computed(() => {
  if (currentEventType.value?.alwaysShowCta) {
    return true;
  }

  return props.item?.isRead ? false : true;
});

function apiMarkAsSeen() {
  if (props.item === undefined) return;

  apiService.markAsSeen(props.item.id).then(() => {
    useDefaultStore().refreshNotifications();
  });
}

function refreshNotifications() {
  store.refreshNotifications();
}

function closeNotifications() {
  store.closeNotificationSidebar();
}

async function handleCtaClick(item: NotificationDto) {
  const eventType = item.eventType;

  if (eventType === NotificationType.CampaignEligible) {
    handleCampaignDetailAction(item);

    apiMarkAsSeen();

    refreshNotifications();
    closeNotifications();

    return;
  }

  if (eventType === NotificationType.CampaignNotEligible) {
    // TODO: go to campaigns page and open sidebar for the campaign
    handleCampaingVerificationAction(item);

    apiMarkAsSeen();

    refreshNotifications();
    closeNotifications();

    return;
  }

  if (
    eventType === NotificationType.CampaignJoined ||
    eventType === NotificationType.CampaignEnded
  ) {
    handleCampaignDetailAction(item);

    apiMarkAsSeen();

    refreshNotifications();
    closeNotifications();

    return;
  }

  if (eventType === NotificationType.CampaignMaturity) {
    await queryClient.invalidateQueries({ queryKey: ["listPaychecks"] });
    apiMarkAsSeen();
    refreshNotifications();
    closeNotifications();
    return;
  }

  if (eventType === NotificationType.NewEligiblePosts) {
    await useRouter().push("/");
    apiMarkAsSeen();
    refreshNotifications();
    closeNotifications();
    return;
  }

  if (eventType === NotificationType.TransactionVerifying) {
    globalEmit(
      "transaction::showOtpModal",
      item?.callToActionParam?.withdrawalId,
    );
    apiMarkAsSeen();
    refreshNotifications();
    return;
  }

  if (eventType === NotificationType.TransactionCompleted) {
    apiMarkAsSeen();
    refreshNotifications();
    closeNotifications();
    return;
  }

  if (eventType === NotificationType.TermsAndConditions) {
    apiMarkAsSeen();
    refreshNotifications();
    return;
  }

  if (eventType === NotificationType.AdHoc) {
    apiMarkAsSeen();
    refreshNotifications();
    return;
  }

  if (eventType === NotificationType.VlayerBadge) {
    apiMarkAsSeen();
    refreshNotifications();
    return;
  }
}

const textParts = computed(() => {
  if (!props.item) return [];

  // @ts-expect-error
  const campaignTitle = props.item.callToActionParam?.campaignTitle ?? "";
  const eventText = props.item.eventText ?? "";

  if (!campaignTitle) return [{ text: eventText, isTitle: false }];

  const parts = [];
  let remainingText = eventText;

  while (remainingText.includes(campaignTitle)) {
    const index = remainingText.indexOf(campaignTitle);
    if (index > 0) {
      // Add text before the campaignTitle
      parts.push({ text: remainingText.slice(0, index), isTitle: false });
    }
    // Add the campaignTitle
    parts.push({ text: campaignTitle, isTitle: true });

    // Update the remaining text
    remainingText = remainingText.slice(index + campaignTitle.length);
  }

  // Add any remaining text after the last occurrence of campaignTitle
  if (remainingText) {
    parts.push({ text: remainingText, isTitle: false });
  }

  return parts;
});

const vlayerCta = computed(() => {
  if (props.item?.eventType !== NotificationType.VlayerBadge) return null;

  const ctaJson = props.item.callToActionParam as unknown as any;
  return {
    cta_label: ctaJson.cta_label ?? "(missing)",
    cta_url: ctaJson.cta_url ?? "(missing)",
  };
});
</script>

<template>
  <div v-if="item">
    <div class="flex lg:items-center justify-between mb-2 gap-10 lg:gap-20">
      <div
        class=""
        :class="{ 'text-black/50': item.isRead }"
        style="max-width: 350px"
      >
        <span
          v-if="!item.isRead"
          class="bg-red inline-block w-2 h-2 mr-2 rounded-full shrink-0"
        ></span>
        <span v-if="item.eventType == NotificationType.TermsAndConditions">
          Welcome to Evangelist. By signing up, you agreed to our
          <NuxtLink class="underline" to="/articles/terms" target="_blank"
            >User Terms</NuxtLink
          >
          and
          <NuxtLink
            class="underline"
            to="/articles/privacy-policy"
            target="_blank"
            >Privacy Policy</NuxtLink
          >.
        </span>
        <span v-else>
          <span v-for="(part, index) in textParts" :key="index">
            <span v-if="part.isTitle" class="text-[#6659FF]">{{
              part.text
            }}</span>
            <span v-else v-html="part.text"></span>
          </span>
        </span>
        <div
          v-if="item.eventType == NotificationType.CampaignJoined"
          class="my-3"
        >
          <NotificationCampaignProgressBar :params="item.callToActionParam" />
        </div>
      </div>
      <div class="w-8 h-8 shrink-0">
        <BrandsIcon
          v-if="item?.callToActionParam?.advertiserTheme"
          :theme="item.callToActionParam.advertiserTheme"
        />
        <div
          v-else-if="
            [NotificationType.NewEligiblePosts].includes(
              item.eventType as NotificationType,
            )
          "
          class="bg-black rounded-icon text-white"
        >
          <IconsTweetIcon />
        </div>
        <div
          v-else-if="
            [
              NotificationType.TransactionVerifying,
              NotificationType.TransactionCompleted,
            ].includes(item.eventType as NotificationType)
          "
          class="bg-purple-light rounded-icon"
        >
          <IconsWalletIcon />
        </div>
        <BrandsEvangelistIcon v-else class="w-full h-full" />
      </div>
    </div>
    <div class="flex items-center justify-between mt-5 lg:mt-0">
      <a
        v-if="item.eventType == NotificationType.TransactionCompleted"
        :href="item?.callToActionParam?.externalTransactionUrl"
        target="_blank"
        class="flex items-center gap-1 text-purple font-semibold"
        @click="handleCtaClick(item)"
      >
        See on Blockchain
        <NewTabIcon />
      </a>
      <a
        v-if="item.eventType == NotificationType.VlayerBadge"
        :href="vlayerCta?.cta_url"
        target="_blank"
        class="flex items-center gap-1 text-purple font-semibold"
        @click="handleCtaClick(item)"
      >
        {{ vlayerCta?.cta_label }}
        <NewTabIcon />
      </a>
      <div v-else>
        <button
          v-if="computedShowCtaLabel"
          class="text-purple hover:underline cursor-pointer"
          @click="handleCtaClick(item)"
        >
          {{ computedCtaLabel }}
        </button>
      </div>
      <div class="text-black/50">
        <TimeAgo :date="item.dateCreated" />
      </div>
    </div>
  </div>
</template>
